<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-format-list-bulleted-square me-2"></i>{{ $t('page.service_catalog', 'Service Catalog') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-amber" @click="showBulk = true"><i class="mdi mdi-percent-outline me-1"></i>Bulk Price Update</button>
          <button class="hms-btn hms-btn-blue" @click="openUpload"><i class="mdi mdi-file-upload-outline me-1"></i>Bulk Upload</button>
          <button class="hms-btn hms-btn-primary" @click="openAdd"><i class="mdi mdi-plus me-1"></i>New Service</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading"><i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i></button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filters.status='active'; loadAll()" :class="{ selected: filters.status==='active' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.active_count || 0 }}</div>
          <div class="hms-stat-lbl">Active Services</div>
        </div>
        <div class="hms-stat" @click="filters.status='inactive'; loadAll()" :class="{ selected: filters.status==='inactive' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.inactive_count || 0 }}</div>
          <div class="hms-stat-lbl">Inactive</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ stats.total || 0 }}</div>
          <div class="hms-stat-lbl">Total Services</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ byCategory.length }}</div>
          <div class="hms-stat-lbl">Departments in Use</div>
        </div>
      </div>

      <!-- Category breakdown -->
      <div class="hms-card mb-3" v-if="byCategory.length">
        <div class="hms-card-title"><i class="mdi mdi-chart-donut me-1"></i>By Department</div>
        <div class="hms-cat-grid">
          <div v-for="c in byCategory" :key="c.category"
               class="hms-cat" :class="{ active: filters.category === c.category }"
               @click="filters.category = (filters.category === c.category ? '' : c.category); loadAll()">
            <i class="mdi" :class="catIcon(c.category)"></i>
            <div>
              <div class="hms-cat-num">{{ c.cnt }}</div>
              <div class="hms-cat-lbl">{{ formatCategory(c.category) }}</div>
              <div class="hms-muted-sm">avg KSh {{ formatNum(c.avg_price) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters + table -->
      <div class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="filters.search" placeholder="Search service name or code..." @input="debouncedLoad" />
          </div>
          <select class="hms-input hms-filter" v-model="filters.category" @change="loadAll">
            <option value="">All departments</option>
            <option v-for="c in categories" :key="c" :value="c">{{ formatCategory(c) }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.status" @change="loadAll">
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </select>
          <span class="hms-muted ms-auto">{{ services.length }} services</span>
        </div>

        <div v-if="loading && !services.length" class="hms-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="services.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Code</th>
              <th>Service Name</th>
              <th>Department</th>
              <th>Scope</th>
              <th class="ta-r">Cash Price</th>
              <th class="ta-r">Insurance Price</th>
              <th>Tax</th>
              <th>Status</th>
              <th></th>
            </tr></thead>
            <tbody>
              <tr v-for="s in services" :key="s.service_id" :class="{ inactive: s.status !== 'active' }">
                <td><span class="hms-mono">{{ s.service_code || '—' }}</span></td>
                <td><strong>{{ s.service_name }}</strong></td>
                <td><span class="hms-badge hms-badge-info"><i class="mdi me-1" :class="catIcon(s.category)"></i>{{ formatCategory(s.category) }}</span></td>
                <td><span class="hms-muted-sm">{{ s.branch_name || 'All branches' }}</span></td>
                <td class="ta-r"><strong>KSh {{ formatNum(s.price) }}</strong></td>
                <td class="ta-r">{{ s.insurance_price ? 'KSh ' + formatNum(s.insurance_price) : '—' }}</td>
                <td><span class="hms-muted-sm">{{ s.is_taxable == 1 ? s.tax_rate + '%' : '—' }}</span></td>
                <td><span class="hms-badge" :class="s.status === 'active' ? 'hms-badge-success' : 'hms-badge-neutral'">{{ s.status }}</span></td>
                <td class="d-flex gap-1">
                  <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEdit(s)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="toggleStatus(s)" :title="s.status === 'active' ? 'Deactivate' : 'Activate'">
                    <i class="mdi" :class="s.status === 'active' ? 'mdi-pause' : 'mdi-play'"></i>
                  </button>
                  <button class="hms-btn hms-btn-sm hms-btn-danger" @click="confirmDelete(s)" title="Delete"><i class="mdi mdi-trash-can-outline"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-format-list-bulleted-square"></i>No services match.</div>
      </div>

      <!-- ADD / EDIT MODAL -->
      <div class="hms-modal-overlay" v-if="showForm" @click.self="showForm=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="form.service_id ? 'mdi-pencil' : 'mdi-plus-circle'"></i>{{ form.service_id ? ' Edit Service' : ' New Service' }}</span>
            <button class="hms-modal-close" @click="showForm=false">&times;</button>
          </div>
          <div v-if="formError" class="hms-alert hms-alert-danger">{{ formError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="hms-label">Service Name *</label>
              <input class="hms-input" v-model="form.service_name" placeholder="e.g. CT Scan with contrast" />
            </div>
            <div class="col-md-4"><label class="hms-label">Service Code</label>
              <input class="hms-input" v-model="form.service_code" placeholder="CT-001" />
            </div>
            <div class="col-md-6"><label class="hms-label">Department *</label>
              <select class="hms-input" v-model="form.category">
                <option value="consultation">Consultation</option>
                <option value="procedure">Procedure</option>
                <option value="lab">Lab</option>
                <option value="radiology">Radiology</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="ward">Ward</option>
                <option value="theatre">Theatre</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Scope</label>
              <select class="hms-input" v-model.number="form.all_branches">
                <option :value="0">This branch only</option>
                <option :value="1">All branches (hospital-wide)</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Cash Price (KSh) *</label>
              <input type="number" class="hms-input" v-model.number="form.price" step="1" min="0" />
            </div>
            <div class="col-md-6"><label class="hms-label">Insurance Price (KSh)</label>
              <input type="number" class="hms-input" v-model.number="form.insurance_price" step="1" min="0" placeholder="Leave blank to use cash price" />
            </div>
            <div class="col-md-4"><label class="hms-toggle mt-md-4">
              <input type="checkbox" :checked="form.is_taxable == 1" @change="form.is_taxable = $event.target.checked ? 1 : 0" />
              <span>Taxable (VAT)</span>
            </label></div>
            <div class="col-md-4" v-if="form.is_taxable == 1"><label class="hms-label">Tax Rate (%)</label>
              <input type="number" class="hms-input" v-model.number="form.tax_rate" step="0.01" min="0" max="100" />
            </div>
            <div class="col-md-4" v-if="form.service_id"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="form.status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveService" :disabled="processing || !form.service_name">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (form.service_id ? 'Save Changes' : 'Create Service') }}
          </button>
        </div>
      </div>

      <!-- BULK UPLOAD MODAL -->
      <div class="hms-modal-overlay" v-if="showUpload" @click.self="showUpload=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-file-upload-outline me-1"></i>Bulk Upload Services</span>
            <button class="hms-modal-close" @click="showUpload=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Upload a CSV with columns: <strong>service_name, service_code, department, price, insurance_price</strong>.
            Department must be one of: {{ categories.join(', ') }}. Duplicate codes/names are skipped automatically.
          </div>
          <div class="d-flex gap-2 mb-3 flex-wrap">
            <button class="hms-btn hms-btn-blue" @click="downloadTemplate"><i class="mdi mdi-download me-1"></i>Download template</button>
            <label class="hms-btn hms-btn-amber m-0">
              <i class="mdi mdi-paperclip me-1"></i>{{ uploadFileName || 'Choose CSV file' }}
              <input type="file" accept=".csv,text/csv" style="display:none;" @change="onUploadFile" />
            </label>
          </div>
          <div v-if="uploadError" class="hms-alert hms-alert-danger">{{ uploadError }}</div>
          <div v-if="uploadRows.length" class="mb-3">
            <div class="hms-muted mb-2">{{ uploadRows.length }} row(s) parsed — preview (first 8):</div>
            <div style="overflow-x:auto;">
              <table class="hms-table">
                <thead><tr><th>Service Name</th><th>Code</th><th>Department</th><th class="ta-r">Price</th><th class="ta-r">Insurance</th></tr></thead>
                <tbody>
                  <tr v-for="(u, i) in uploadRows.slice(0, 8)" :key="i">
                    <td>{{ u.service_name }}</td>
                    <td><span class="hms-mono">{{ u.service_code || '—' }}</span></td>
                    <td>{{ formatCategory(u.category) }}</td>
                    <td class="ta-r">{{ formatNum(u.price) }}</td>
                    <td class="ta-r">{{ u.insurance_price !== '' ? formatNum(u.insurance_price) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="uploadResult" class="hms-info-banner mb-3">
            <i class="mdi mdi-check-circle me-1"></i>{{ uploadResult.created }} created, {{ uploadResult.skipped.length }} skipped.
            <div v-for="(sk, i) in uploadResult.skipped.slice(0, 10)" :key="i" class="hms-muted-sm">{{ sk }}</div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doBulkUpload" :disabled="processing || !uploadRows.length">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Uploading...' : 'Upload ' + uploadRows.length + ' service(s)' }}
          </button>
        </div>
      </div>

      <!-- BULK UPDATE MODAL -->
      <div class="hms-modal-overlay" v-if="showBulk" @click.self="showBulk=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-percent-outline me-1"></i>Bulk Price Update</span>
            <button class="hms-modal-close" @click="showBulk=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Adjust all <strong>active</strong> services in a department at once. Useful for annual tariff revisions or inflation adjustments.
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Department *</label>
              <select class="hms-input" v-model="bulk.category">
                <option value="">— Select —</option>
                <option v-for="c in categories" :key="c" :value="c">{{ formatCategory(c) }}</option>
              </select>
              <div class="hms-muted-sm mt-1" v-if="bulk.category">
                Will affect {{ servicesInCategory(bulk.category) }} active services.
              </div>
            </div>
            <div class="col-md-6"><label class="hms-label">Apply to</label>
              <select class="hms-input" v-model="bulk.field">
                <option value="price">Cash price</option>
                <option value="insurance_price">Insurance price</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Mode</label>
              <select class="hms-input" v-model="bulk.mode">
                <option value="percent">% change</option>
                <option value="absolute">KSh absolute change</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Value *</label>
              <input type="number" class="hms-input" v-model.number="bulk.value" step="0.01" :placeholder="bulk.mode === 'percent' ? 'e.g. 10 for +10%, -5 for −5%' : 'e.g. 100 for +KSh 100'" />
              <div class="hms-muted-sm mt-1" v-if="bulk.value !== 0 && bulk.value !== ''">
                Sample: KSh 1,000 →
                <strong>KSh {{ bulk.mode === 'percent' ? formatNum(1000 * (1 + bulk.value/100)) : formatNum(1000 + bulk.value) }}</strong>
              </div>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showBulk=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="applyBulk" :disabled="processing || !bulk.category || !bulk.value">Apply Update</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] { --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;--hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;--hms-br:#2a2a2a;--hms-input-br:#333;--hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] { --hms-h:#1a1a1a;--hms-s:#555;--hms-m:#777;--hms-bg1:#fff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#fff;--hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.ta-r { text-align: right; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-success { color: var(--hms-success); } .hms-stat-warning { color: var(--hms-warning); } .hms-stat-info { color: var(--hms-info); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* Category cards */
.hms-cat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; }
.hms-cat { display: flex; gap: 10px; align-items: center; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; cursor: pointer; }
.hms-cat:hover, .hms-cat.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-cat > i { font-size: 22px; color: var(--hms-primary); }
.hms-cat-num { color: var(--hms-h); font-weight: 800; font-size: 18px; line-height: 1; }
.hms-cat-lbl { color: var(--hms-s); font-size: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr.inactive td { opacity: 0.6; }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const CAT_ICONS = {
  consultation: 'mdi-account-tie',
  procedure:    'mdi-medical-bag',
  lab:          'mdi-flask',
  radiology:    'mdi-radioactive',
  pharmacy:     'mdi-pill',
  ward:         'mdi-bed',
  theatre:      'mdi-medical-bag',
  other:        'mdi-shape-outline'
};

export default {
  name: "ServiceCatalog",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      services: [], stats: {}, byCategory: [],
      filters: { category: '', status: 'active', search: '' },
      _searchTimer: null,
      categories: ['consultation','procedure','lab','radiology','pharmacy','ward','theatre','other'],

      showForm: false, formError: '',
      form: this.empty(),

      showBulk: false,
      bulk: { category: '', field: 'price', mode: 'percent', value: 0 },

      showUpload: false, uploadRows: [], uploadError: '', uploadFileName: '', uploadResult: null,

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    showToast(msg, isErr) {
      this.toastMsg = msg; this.toastError = !!isErr;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var s = this; this.toastTimer = setTimeout(() => s.toastMsg = '', 2400);
    },
    formatNum(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    formatCategory(c) { return ({ consultation:'Consultation', procedure:'Procedure', lab:'Lab', radiology:'Radiology', pharmacy:'Pharmacy', ward:'Ward', theatre:'Theatre', other:'Other' })[c] || c; },
    catIcon(c) { return CAT_ICONS[c] || 'mdi-shape-outline'; },
    servicesInCategory(c) { var x = this.byCategory.find(b => b.category === c); return x ? x.cnt : 0; },
    empty() { return { service_id: null, service_name: '', service_code: '', category: 'consultation', price: 0, insurance_price: '', is_taxable: 0, tax_rate: 0, status: 'active', all_branches: 0 }; },

    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var s = this; this._searchTimer = setTimeout(() => s.loadAll(), 350);
    },
    async loadAll() {
      this.loading = true;
      try {
        var p = Object.assign({ function: "getServiceCatalog", all_branches: 1 }, this.filters);
        var r = await executeJson(p, hms);
        if (r.data && r.data.success) {
          this.services   = (r.data.data && r.data.data.Services)   || [];
          this.stats      = (r.data.data && r.data.data.Stats)      || {};
          this.byCategory = (r.data.data && r.data.data.ByCategory) || [];
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    openAdd() { this.form = this.empty(); this.formError = ''; this.showForm = true; },
    openEdit(s) {
      this.form = {
        service_id: s.service_id, service_name: s.service_name, service_code: s.service_code || '',
        category: s.category, price: parseFloat(s.price) || 0,
        insurance_price: s.insurance_price !== null && s.insurance_price !== '' ? parseFloat(s.insurance_price) : '',
        is_taxable: parseInt(s.is_taxable) || 0, tax_rate: parseFloat(s.tax_rate) || 0,
        status: s.status, all_branches: s.branch_id == null ? 1 : 0
      };
      this.formError = ''; this.showForm = true;
    },
    async saveService() {
      this.formError = ''; this.processing = true;
      try {
        var fn = this.form.service_id ? 'updateService' : 'createService';
        var r = await executeJson(Object.assign({ function: fn }, this.form), hms);
        if (r.data && r.data.success) { this.showToast(this.form.service_id ? 'Updated' : 'Created'); this.showForm = false; await this.loadAll(); }
        else this.formError = r.data.message || 'Failed';
      } catch(e) { this.formError = 'Connection error'; }
      this.processing = false;
    },
    async toggleStatus(s) {
      try {
        var r = await executeJson({ function: 'toggleServiceStatus', service_id: s.service_id }, hms);
        if (r.data && r.data.success) { this.showToast('Status updated'); await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },
    async confirmDelete(s) {
      if (!confirm('Delete service "' + s.service_name + '"?\n\nIt will be soft-deleted (recoverable from DB).')) return;
      try {
        var r = await executeJson({ function: 'deleteService', service_id: s.service_id }, hms);
        if (r.data && r.data.success) { this.showToast('Deleted'); await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    openUpload() {
      this.showUpload = true;
      this.uploadRows = []; this.uploadError = ''; this.uploadFileName = ''; this.uploadResult = null;
    },
    downloadTemplate() {
      var csv = "service_name,service_code,department,price,insurance_price\n"
              + "General Consultation,CONS-001,consultation,500,400\n"
              + "Full Haemogram,LAB-001,lab,800,\n";
      var blob = new Blob([csv], { type: 'text/csv' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'services_template.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    },
    parseCsvLine(line) {
      var out = [], cur = '', inQ = false;
      for (var i = 0; i < line.length; i++) {
        var ch = line[i];
        if (inQ) {
          if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
          else if (ch === '"') inQ = false;
          else cur += ch;
        } else {
          if (ch === '"') inQ = true;
          else if (ch === ',') { out.push(cur); cur = ''; }
          else cur += ch;
        }
      }
      out.push(cur);
      return out;
    },
    onUploadFile(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      this.uploadFileName = file.name;
      this.uploadError = ''; this.uploadRows = []; this.uploadResult = null;
      var self = this;
      var reader = new FileReader();
      reader.onload = function() {
        try {
          var lines = String(reader.result).split(/\r?\n/).filter(function(l){ return l.trim() !== ''; });
          if (lines.length < 2) { self.uploadError = 'CSV has no data rows.'; return; }
          var head = self.parseCsvLine(lines[0]).map(function(h){ return h.trim().toLowerCase(); });
          var idx = {
            name:  head.indexOf('service_name'),
            code:  head.indexOf('service_code'),
            dept:  head.indexOf('department') >= 0 ? head.indexOf('department') : head.indexOf('category'),
            price: head.indexOf('price'),
            ins:   head.indexOf('insurance_price')
          };
          if (idx.name < 0 || idx.price < 0) { self.uploadError = 'CSV must have service_name and price columns.'; return; }
          var rows = [];
          for (var i = 1; i < lines.length; i++) {
            var c = self.parseCsvLine(lines[i]);
            var dept = (idx.dept >= 0 ? (c[idx.dept] || '') : '').trim().toLowerCase();
            rows.push({
              service_name: (c[idx.name] || '').trim(),
              service_code: (idx.code >= 0 ? (c[idx.code] || '') : '').trim(),
              category: self.categories.indexOf(dept) >= 0 ? dept : 'other',
              price: parseFloat(c[idx.price]) || 0,
              insurance_price: (idx.ins >= 0 && (c[idx.ins] || '').trim() !== '') ? (parseFloat(c[idx.ins]) || 0) : ''
            });
          }
          self.uploadRows = rows;
          if (rows.length > 500) self.uploadError = 'Maximum 500 rows per upload — split the file.';
        } catch (err) { self.uploadError = 'Could not parse the file.'; }
      };
      reader.readAsText(file);
      e.target.value = '';
    },
    async doBulkUpload() {
      if (!this.uploadRows.length || this.uploadRows.length > 500) return;
      this.processing = true; this.uploadError = ''; this.uploadResult = null;
      try {
        var r = await executeJson({ function: 'bulkCreateServices', services: JSON.stringify(this.uploadRows) }, hms);
        if (r.data && r.data.success) {
          this.uploadResult = r.data.data;
          this.uploadRows = []; this.uploadFileName = '';
          this.showToast(r.data.message);
          await this.loadAll();
        } else this.uploadError = (r.data && r.data.message) || 'Failed';
      } catch(e) { this.uploadError = 'Connection error'; }
      this.processing = false;
    },

    async applyBulk() {
      if (!confirm('Apply ' + (this.bulk.mode === 'percent' ? this.bulk.value + '%' : 'KSh ' + this.bulk.value) + ' to all active ' + this.formatCategory(this.bulk.category) + ' services?\n\nThis cannot be undone.')) return;
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: 'bulkUpdateServicePrices' }, this.bulk), hms);
        if (r.data && r.data.success) { this.showToast('Bulk update applied'); this.showBulk = false; await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    }
  }
};
</script>
